/**
 * Authentication configuration.
 *
 * Centralizes auth-related constants that were previously scattered
 * across components (e.g., magic link whitelist).
 */

/**
 * Magic link authentication configuration.
 */
export const MAGIC_LINK_CONFIG = {
  /**
   * Allowed redirect paths after magic link authentication.
   * Security measure to prevent open redirect attacks.
   */
  allowedRedirects: [
    '/planning',
    '/ideas',
    '/report',
    '/social-media',
    '/creator',
    '/editor',
    // Covers per-client portal sub-routes the share buttons hand out
    // (e.g. /client/<id>/ideas, /client/<id>/posts, /client/<id>/reel).
    '/client',
  ],

  /**
   * Delay in ms before redirecting after successful authentication.
   * Gives user time to see the success message.
   */
  redirectDelay: 1500,
} as const;

/**
 * Query-string keys that are classic open-redirect pivots. Blocked even when
 * the base path is whitelisted — otherwise `/foto?redirect=https://evil`
 * would slip through. Mirrors the backend
 * `_BLOCKED_NESTED_REDIRECT_KEYS` in `magic_link_service.py`.
 */
const BLOCKED_NESTED_REDIRECT_KEYS = new Set([
  'redirect',
  'redirect_uri',
  'redirect_to',
  'return_to',
  'next',
  'url',
]);

/**
 * Where an admin may land after Google sign-in (spec 2026-10-15). Kept apart
 * from the portal list so an admin path never feeds a magic-link request and
 * a portal path never becomes an admin landing.
 */
export const ADMIN_REDIRECT_PREFIXES: readonly string[] = [
  '/tasks',
  '/clients',
  '/leads',
  '/finance',
  '/places',
  '/setup',
  '/app',
];

/**
 * Validate a redirect path against the whitelist.
 *
 * Rules (mirror of backend `_validate_redirect_path`):
 * - Must be a relative path (leading slash added if missing).
 * - The base path (before '?') must either equal a whitelisted entry or
 *   start with a whitelisted entry followed by '/' (so
 *   `/creator/<clientId>?briefId=...` is accepted when `/creator`
 *   is whitelisted).
 * - Any nested redirect-like query parameter (redirect, next, url, ...)
 *   causes rejection. Case-insensitive.
 *
 * Returns the validated path (including any safe query string) or null.
 */
export function validateRedirectPath(path: string | null): string | null {
  return validateAgainst(path, MAGIC_LINK_CONFIG.allowedRedirects);
}

/** The admin twin of validateRedirectPath, with the same nested-redirect rules. */
export function validateAdminRedirectPath(path: string | null): string | null {
  return validateAgainst(path, ADMIN_REDIRECT_PREFIXES);
}

function validateAgainst(path: string | null, allowed: readonly string[]): string | null {
  if (!path) return null;

  const normalized = path.startsWith('/') ? path : `/${path}`;

  // Split path and query manually; never feed a full URL to a URL parser.
  const queryIndex = normalized.indexOf('?');
  const base = queryIndex === -1 ? normalized : normalized.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : normalized.slice(queryIndex + 1);

  const baseMatches = allowed.some(
    (entry) => base === entry || base.startsWith(`${entry}/`)
  );
  if (!baseMatches) return null;

  if (query) {
    const params = new URLSearchParams(query);
    const keys: string[] = [];
    params.forEach((_value, key) => {
      keys.push(key);
    });
    for (const key of keys) {
      if (BLOCKED_NESTED_REDIRECT_KEYS.has(key.toLowerCase())) {
        return null;
      }
    }
  }

  return normalized;
}

/**
 * Admin token refresh configuration.
 */
export const ADMIN_TOKEN_CONFIG = {
  /**
   * Interval between periodic token validation (in milliseconds).
   */
  validationIntervalMs: 15 * 60 * 1000, // 15 minutes

  /**
   * Threshold for proactive token refresh during periodic checks.
   */
  proactiveRefreshThresholdMs: 10 * 60 * 1000, // 10 minutes

  /**
   * Threshold for activity-based token refresh.
   */
  activityRefreshThresholdMs: 5 * 60 * 1000, // 5 minutes

  /**
   * Debounce time for activity events.
   */
  activityDebounceMs: 1000,
} as const;

/**
 * Portal token expiration.
 * These match the backend JWT configuration.
 */
export const PORTAL_TOKEN_CONFIG = {
  /**
   * Password-based "access" tokens expire after 90 days.
   */
  accessTokenDays: 90,

  /**
   * Legacy magic link "session" tokens expire after 30 days.
   */
  sessionTokenDays: 30,

  /**
   * New magic link "portal_access" tokens expire after 60 minutes.
   */
  portalAccessTokenMinutes: 60,

  /**
   * Refresh tokens last up to 30 days (capped by session expiry).
   */
  refreshTokenDays: 30,

  /**
   * Trigger proactive refresh when access token has less than this many minutes left.
   */
  refreshThresholdMinutes: 5,
} as const;
